/**
 * Sprint 12+M5.7 (19.07.2026) — Wiederverwendbarer Share-Button fuer den
 * Fokustyp. Extraktion aus `PionierQuickCheck.jsx#ResultCard.handleShare`
 * (unveraendert im Verhalten), damit die Funktion auch im PionierCockpit
 * direkt neben dem Kurzprofil sichtbar ist.
 *
 * Andreas' Curiosity-Text-Template + Native-Share (mobile) mit Bild-Datei
 * oder Download-Fallback (Desktop). Backend-Rendering via
 * POST /api/share/quickcheck-image?fokustyp={de-slug} bleibt gleich.
 */
import React, { useState } from "react";
import { API } from "@/lib/api";
import { FOKUSTYP_PROFILES } from "@/lib/fokustypProfiles";
import {
  Share2,
  Download,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const SLUG_EN_TO_DE = {
  bee: "biene",
  owl: "eule",
  cat: "katze",
  dog: "hund",
};

function downloadBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export default function ShareFokustypButton({
  fokustyp,
  testId = "share-fokustyp-button",
  variant = "pill",
}) {
  // fokustyp kann DE ("eule") oder EN ("owl") kommen — beides tolerieren.
  const fokustypDe = SLUG_EN_TO_DE[fokustyp] || fokustyp;
  const fokustypEn = fokustyp in FOKUSTYP_PROFILES ? fokustyp : null;
  const profile =
    (fokustypEn && FOKUSTYP_PROFILES[fokustypEn]) ||
    FOKUSTYP_PROFILES[Object.keys(SLUG_EN_TO_DE).find((k) => SLUG_EN_TO_DE[k] === fokustypDe)] ||
    null;

  const [state, setState] = useState("idle"); // idle | loading | done | error

  async function handleShare() {
    if (state === "loading" || !fokustypDe) return;
    setState("loading");
    try {
      const url = `${API}/share/quickcheck-image?fokustyp=${encodeURIComponent(fokustypDe)}`;
      const resp = await fetch(url, { method: "POST", credentials: "include" });
      if (!resp.ok) throw new Error(`render_failed_${resp.status}`);
      const blob = await resp.blob();
      const file = new File(
        [blob],
        `mein-fokustyp-${fokustypDe}.png`,
        { type: "image/png" }
      );
      const shareText =
        `Gerade habe ich einen verblüffenden Test gemacht und einiges über mich erfahren. ` +
        `Wusstest du, dass sich viele Kommunikationsprobleme auf nur vier typische ` +
        `Reaktionsmuster zurückführen lassen? Mein Muster ist die ${profile?.name || fokustypDe}. ` +
        `Möchtest du deins herausfinden? Lass mich wissen, was rausgekommen ist.`;
      const shareUrl = "https://flex4success.effektiv-kommunizieren.de";

      let usedChannel = "download";
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            title: `Mein Fokustyp: ${profile?.name || fokustypDe}`,
            text: shareText,
            url: shareUrl,
          });
          usedChannel = "native";
        } catch (err) {
          if (err && err.name === "AbortError") {
            setState("idle");
            return;
          }
          downloadBlob(blob, `mein-fokustyp-${fokustypDe}.png`);
          usedChannel = "download";
        }
      } else {
        downloadBlob(blob, `mein-fokustyp-${fokustypDe}.png`);
        usedChannel = "download";
      }

      // Fire-and-forget Tracking
      try {
        await fetch(`${API}/share/track`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fokustyp: fokustypDe, channel: usedChannel }),
        });
      } catch {
        /* still ignorieren */
      }
      setState("done");
      setTimeout(() => setState("idle"), 2500);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 2500);
    }
  }

  const isPill = variant === "pill";
  const cls = isPill
    ? "inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 text-sm font-medium px-4 py-2 transition-colors"
    : "inline-flex items-center gap-2 rounded-xl bg-[#4563BB] hover:bg-[#3a54a2] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 transition-colors";

  return (
    <button
      type="button"
      onClick={handleShare}
      disabled={state === "loading" || !fokustypDe}
      data-testid={testId}
      className={cls}
    >
      {state === "loading" ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Bild wird vorbereitet …</span>
        </>
      ) : state === "done" ? (
        <>
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span>Fertig</span>
        </>
      ) : state === "error" ? (
        <>
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span>Nochmal versuchen</span>
        </>
      ) : (
        <>
          {typeof navigator !== "undefined" && navigator.share ? (
            <Share2 className="w-4 h-4" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          <span>Meinen Fokustyp teilen</span>
        </>
      )}
    </button>
  );
}
